import { useState } from "react";
import {
  Box,
  Text,
  useColorModeValue,
  VStack,
  HStack,
  Tag,
  IconButton,
  useClipboard,
  useToast,
  Tooltip,
  Icon,
  Textarea,
  Button,
} from "@chakra-ui/react";
import {
  ChatMessage as ChatMessageType,
  ChatSession,
  ChatSessionType,
} from "../../types";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  CopyIcon,
  CheckIcon,
  RepeatIcon,
  EditIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useChatStore } from "../../stores/chatStore";
import { useNavigate } from "react-router-dom";
import { CodeBlock } from "./CodeBlock";
import { RiRobot2Line, RiUserLine } from "react-icons/ri";
import { FiColumns, FiCpu } from "react-icons/fi";
import { chatAPI } from "../../services";
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";
import React from "react";
import { useInputStore } from "../../stores/inputStore";
interface ChatMessageProps {
  message: ChatMessageType;
  index: number;
  sessionType: ChatSessionType;
  sessionId?: string;
}

const MemoizedMarkdown = React.memo(
  ({ content, components }: { content: string; components: any }) => {
    console.log("Markdown render:", content.substring(0, 20)); // Log first 20 chars
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    );
  },
  (prevProps, nextProps) => prevProps.content === nextProps.content
);
export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  index,
  sessionType,
  sessionId,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isAI = message.role === "assistant";
  const isUser = message.role === "user";
  const isSystem = message.role === "system";
  const messageColorScheme = {
    user: {
      light: "gray.100",
      dark: "gray.700",
    },
    assistant: {
      light: "transparent",
      dark: "transparent",
    },
    system: {
      light: "blue.100",
      dark: "blue.800",
    },
  };
  const bgColor = useColorModeValue(
    messageColorScheme[message.role].light,
    messageColorScheme[message.role].dark
  );
  const textColor = useColorModeValue("gray.800", "white");
  const textThinkColor = useColorModeValue("gray.500", "gray.400");
  const inlineCodeBg = useColorModeValue("gray.100", "gray.700");
  const inlineCodeColor = useColorModeValue("gray.800", "gray.100");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const { t } = useTranslation();
  const { hasCopied, onCopy } = useClipboard(message.content);
  const toast = useToast();
  const navigate = useNavigate();
  const { createSession, loadSessionMessages } = useChatStore();
  const {
    input: inputStore,
    setInput: setInputStore,
    clearInput,
  } = useInputStore();

  const handleEditSystemPrompt = () => {
    setIsEditing(true);
  };

  const handleDeleteMessage = async () => {
    if (!message.id || !sessionId) return;
    setIsSubmitting(true);
    await chatAPI.deleteMessage(sessionId, message.id);
    loadSessionMessages(sessionId);
    toast({
      title: t("chat.messageDeleted"),
      status: "success",
      duration: 1000,
      isClosable: true,
    });
  };

  const handleSaveEdit = async () => {
    if (!message.id || !sessionId) return;
    setIsSubmitting(true);
    try {
      await chatAPI.updateSystemMessage(
        { id: sessionId },
        message.id,
        editContent
      );
      setIsEditing(false);
      // Update the message in the UI
      message.content = editContent;
      toast({
        title: t("chat.systemMessageUpdated"),
        status: "success",
        duration: 1000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error updating system message:", error);
      toast({
        title: t("chat.errors.updateFailed"),
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(message.content);
  };

  const handleCopy = () => {
    onCopy();
    toast({
      title: t("chat.messageCopied"),
      status: "success",
      duration: 1000,
      isClosable: true,
    });
  };

  const handleReprompt = async () => {
    const session = await createSession(ChatSessionType.STANDARD);
    setInputStore(message.content);
    navigate(`/chat/${session.id}`);
    toast({
      title: t("chat.repromptCreated"),
      status: "success",
      duration: 1000,
      isClosable: true,
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const MathWrapper = ({ children }: { children: string }) => {
    try {
      return <BlockMath math={children} />;
    } catch (error) {
      console.error("Math parsing error:", error);
      return <Text color="red.500">Error rendering math: {children}</Text>;
    }
  };
  const components = {
    code: ({ inline, className, children }: any) => {
      const value = String(children).replace(/\n$/, "");

      // Handle inline single backtick code
      if (inline) {
        return (
          <Text
            as="span"
            bg={inlineCodeBg}
            color={inlineCodeColor}
            px={1.5}
            mx={0.5}
            borderRadius="md"
            fontSize="0.9em"
            fontFamily="monospace"
            display="inline"
            whiteSpace="nowrap"
            verticalAlign="middle"
          >
            {value}
          </Text>
        );
      }

      // Only process as code block if it has a language class (triple backtick)
      const match = /language-(\w+)/.exec(className || "");
      if (!match) return value;

      const language = match[1];
      return <CodeBlock language={language} value={value} />;
    },
    h1: ({ children }: any) => (
      <Text
        fontSize="2xl"
        fontWeight="bold"
        mb={4}
        pb={2}
        borderBottom="2px solid"
        borderColor={textColor}
        fontFamily="system-ui"
      >
        {children}
      </Text>
    ),
    p: ({ children }: any) => {
      // const text = React.Children.toArray(children)
      //   .map((child) => (typeof child === "string" ? child : ""))
      //   .join("");

      // Split text into parts, preserving math blocks and regular text
      //       const parts = text.split(/(\[[\s\S]*?\])/g);
      // //
      //       if (parts.length > 1) {
      //         return (
      //           <Text fontSize="sm" lineHeight="1.5" mb={4} whiteSpace="pre-wrap">
      //             {parts.map((part, index) => {
      //               // Handle block math
      //               if (part.trim().startsWith("[") && part.trim().endsWith("]")) {
      //                 const mathContent = part
      //                   .trim()
      //                   .replace(/^\[/, "")
      //                   .replace(/\]$/, "")
      //                   .trim();
      //                 return <BlockMath key={index} math={mathContent} />;
      //               }

      //               // Handle inline math in remaining text
      //               const inlineParts = part.split(/(\([^)]+\))/g);
      //               return inlineParts.map((inlinePart, inlineIndex) => {
      //                 if (inlinePart.startsWith("(") && inlinePart.endsWith(")")) {
      //                   const mathContent = inlinePart.slice(1, -1).trim();
      //                   return (
      //                     <InlineMath
      //                       key={`${index}-${inlineIndex}`}
      //                       math={mathContent}
      //                     />
      //                   );
      //                 }
      //                 // Regular text
      //                 return inlinePart;
      //               });
      //             })}
      //           </Text>
      //         );
      //       }

      // Regular text without math
      return (
        <Text fontSize="sm" lineHeight="1.5" mb={4} whiteSpace="pre-wrap">
          {children}
        </Text>
      );
    },
    blockquote: ({ children }: any) => (
      <Text fontSize="sm" lineHeight="1.5" mb={4} color={textThinkColor}>
        {children}
      </Text>
    ),
    span: ({ children }: any) => {
      const text = String(children);
      if (text.match(/\$(.*)\$/)) {
        const mathContent = text.replace(/\$(.*)\$/, "$1");
        return <InlineMath math={mathContent} />;
      }
      return <span>{children}</span>;
    },
  };

  return (
    <Box
      w="full"
      bg={bgColor}
      p={4}
      borderWidth={1}
      borderColor={borderColor}
      borderRadius="lg"
      color={textColor}
      maxW={{ base: "100%", md: "80%" }}
      alignSelf={isAI ? "flex-start" : "flex-end"}
    >
      <VStack align="stretch" spacing={3} overflowX="auto">
        <HStack spacing={2}>
          <Tag size="sm" colorScheme="gray">
            <HStack>
              <Icon as={FiColumns} />
              <Text>{index + 1}</Text>
            </HStack>
          </Tag>
          <Tag
            size="sm"
            colorScheme={isAI ? "blue" : isUser ? "green" : "gray"}
          >
            {isAI ? (
              <HStack>
                <Icon as={RiRobot2Line} />
                <Text>{t("chat.ai")}</Text>
              </HStack>
            ) : isUser ? (
              <HStack>
                <Icon as={RiUserLine} />
                <Text>{t("chat.you")}</Text>
              </HStack>
            ) : isSystem ? (
              <HStack>
                <Icon as={FiCpu} />
                <Text>{t("chat.system")}</Text>
              </HStack>
            ) : (
              ""
            )}
          </Tag>
          {message.model && (
            <Tag size="sm" colorScheme="purple">
              {message.model}
            </Tag>
          )}
        </HStack>
        <Box className="markdown-content">
          {isEditing ? (
            <VStack spacing={2} align="stretch">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                size="sm"
                resize="vertical"
                rows={4}
              />
              <HStack spacing={2} justify="flex-end">
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  isDisabled={isSubmitting}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  onClick={handleSaveEdit}
                  isLoading={isSubmitting}
                >
                  {t("common.save")}
                </Button>
              </HStack>
            </VStack>
          ) : (
            <MemoizedMarkdown
              content={message.content}
              components={components}
            />
          )}
        </Box>
        <HStack justify="space-between" align="center">
          <Text fontSize="xs" color="gray.500">
            {formatDateTime(message.createdAt)}
          </Text>
          <HStack spacing={1}>
            {(message.role === "user" || message.role === "assistant") && (
              <Tooltip label={t("chat.deleteMessage")} placement="top" hasArrow>
                <IconButton
                  aria-label={t("chat.deleteMessage")}
                  icon={<DeleteIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={handleDeleteMessage}
                />
              </Tooltip>
            )}
            <Tooltip label={t("chat.copyMessage")} placement="top" hasArrow>
              <IconButton
                aria-label={t("chat.copyMessage")}
                icon={hasCopied ? <CheckIcon /> : <CopyIcon />}
                size="xs"
                variant="ghost"
                onClick={handleCopy}
              />
            </Tooltip>
            <Tooltip label={t("chat.reprompt")} placement="top" hasArrow>
              <IconButton
                aria-label={t("chat.reprompt")}
                icon={<RepeatIcon />}
                size="xs"
                variant="ghost"
                onClick={handleReprompt}
              />
            </Tooltip>
            {message.role === "system" &&
              sessionType === ChatSessionType.STANDARD && (
                <Tooltip
                  label={t("chat.editSystemPrompt")}
                  placement="top"
                  hasArrow
                >
                  <IconButton
                    aria-label={t("chat.editSystemPrompt")}
                    icon={<EditIcon />}
                    size="xs"
                    variant="ghost"
                    onClick={handleEditSystemPrompt}
                  />
                </Tooltip>
              )}
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
};
